import {
  Box,
  Heading,
  Image,
  ListItem,
  OrderedList,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
  UnorderedList,
} from '@chakra-ui/react';
import React from 'react';

interface RendererProps {
  snapshot: any;
}

interface StyleMap {
  [name: string]: any;
}

function styleProps(node: any, styles: StyleMap = {}): any {
  const props: any = {};
  const named = typeof node?.style === 'string' ? styles[node.style] : null;
  const merged = { ...(named || {}), ...node };
  if (merged.fontSize) props.fontSize = `${merged.fontSize}px`;
  if (merged.bold) props.fontWeight = 'bold';
  if (merged.italics) props.fontStyle = 'italic';
  if (merged.alignment) props.textAlign = merged.alignment;
  return props;
}

function renderNode(node: any, styles: StyleMap, key: React.Key): React.ReactNode {
  if (node === null || node === undefined) return null;
  if (typeof node === 'string') return <Text key={key}>{node}</Text>;

  if (Array.isArray(node)) {
    return (
      <Stack key={key} spacing={2}>
        {node.map((n, i) => renderNode(n, styles, i))}
      </Stack>
    );
  }

  if (node.style === 'header' || node.style === 'subheader') {
    return (
      <Heading
        key={key}
        as={node.style === 'header' ? 'h2' : 'h3'}
        size={node.style === 'header' ? 'lg' : 'md'}
        {...styleProps(node, styles)}
      >
        {node.text}
      </Heading>
    );
  }

  if (typeof node.text !== 'undefined') {
    if (Array.isArray(node.text)) {
      return (
        <Text key={key} {...styleProps(node, styles)}>
          {node.text.map((t: any, i: number) => (
            <React.Fragment key={i}>{renderInline(t, styles)}</React.Fragment>
          ))}
        </Text>
      );
    }
    // Defensive: if text is an object (e.g. { feeTable: 'plan' }), fall through
    // and try to render it as a structural node instead of stringifying.
    if (node.text && typeof node.text === 'object') {
      return renderNode(node.text, styles, key);
    }
    return (
      <Text key={key} {...styleProps(node, styles)}>
        {String(node.text)}
      </Text>
    );
  }

  if (node.stack) {
    return (
      <Stack key={key} spacing={2}>
        {node.stack.map((n: any, i: number) => renderNode(n, styles, i))}
      </Stack>
    );
  }

  if (node.image && typeof node.image === 'string') {
    return (
      <Image
        key={key}
        src={node.image}
        alt="contract"
        maxW={node.width ? `${node.width}px` : '300px'}
      />
    );
  }

  if (Array.isArray(node.ul)) {
    return (
      <UnorderedList key={key} pl={4} spacing={1}>
        {node.ul.map((item: any, i: number) => (
          <ListItem key={i}>{renderNode(item, styles, i)}</ListItem>
        ))}
      </UnorderedList>
    );
  }

  if (Array.isArray(node.ol)) {
    return (
      <OrderedList key={key} pl={4} spacing={1}>
        {node.ol.map((item: any, i: number) => (
          <ListItem key={i}>{renderNode(item, styles, i)}</ListItem>
        ))}
      </OrderedList>
    );
  }

  if (node.columns && Array.isArray(node.columns)) {
    return (
      <Stack key={key} direction={{ base: 'column', md: 'row' }} spacing={6}>
        {node.columns.map((col: any, i: number) => (
          <Box key={i} flex="1">
            {renderNode(col, styles, i)}
          </Box>
        ))}
      </Stack>
    );
  }

  if (node.table) {
    return (
      <Box key={key} overflowX="auto">
        <Table size="sm">
          <Tbody>
            {node.table.body.map((row: any[], r: number) => (
              <Tr key={r}>
                {row.map((cell, c) => (
                  <Td key={c}>{renderNode(cell, styles, c)}</Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  }

  if (node.content) {
    // Top-level wrapper
    return (
      <Stack key={key} spacing={3}>
        {(Array.isArray(node.content) ? node.content : [node.content]).map((n: any, i: number) =>
          renderNode(n, node.styles || styles, i),
        )}
      </Stack>
    );
  }

  return null;
}

function renderInline(node: any, _styles: StyleMap): React.ReactNode {
  if (typeof node === 'string') return node;
  if (node?.text) return node.text;
  return null;
}

export function ContractRenderer({ snapshot }: RendererProps) {
  if (!snapshot) return <Text>—</Text>;
  const styles = snapshot.styles || {};
  return (
    <Box maxH="60vh" overflowY="auto" px={2} py={2}>
      {renderNode(snapshot, styles, 'root')}
    </Box>
  );
}
